import React, { FC, useCallback, useEffect, useState, useMemo, useContext } from 'react'
import { useParams, useHistory } from 'react-router'
import { toast } from 'react-toastify'

import { Typography, Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import { CrmRolesEnum } from '@sparling/financial-common'
import { tabList } from './tabList'
import { Loader } from '../../../components/Loader'
import { AppStateContext, useRefresh, useRest } from '../../../components/Context'
import { TabsComponent } from '../../../components/Tabs'

import { routes } from '../../../constants/routes'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(6),
  },
  titleWrap: {
    color: theme.palette.primary.light,
  },
  titleId: {
    fontSize: 18,
    lineHeight: 1.25,
    marginTop: 9,
    marginLeft: 10,
  },
  infoWrap: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  section: {
    width: '100%',
  },
}))

export const CompanyProfileTabs: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { idSf: companyIdSf, tab } = useParams() as { [key: string]: string }
  const { getIsRefresh, getUserRole } = useContext(AppStateContext)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [refresh, setRefresh] = useState<boolean | undefined>()

  const hiddenTabLabels = ['Introducer', 'KYC', 'Transactions', 'Trades', 'Balance']

  const baseTabList = !values?.adminId ? tabList.filter((t) => !hiddenTabLabels.includes(t.tabLabel)) : tabList

  const filteredTabList = useMemo(
    () => baseTabList.filter((t) => !t.roles || t.roles.includes(getUserRole?.() as CrmRolesEnum)),
    [getUserRole, baseTabList],
  )

  const activeTab = useMemo(() => {
    const currentTab = filteredTabList.find((t) => t.slug === tab)
    return currentTab?.slug || filteredTabList[0]?.slug || ''
  }, [filteredTabList, tab])

  useEffect(() => {
    if (tab !== activeTab && activeTab) {
      history.replace(`${routes.COMPANIES}/${companyIdSf}/${activeTab}`)
    }
  }, [activeTab, tab, history, companyIdSf])

  const loadData = useCallback(
    async () => {
      let cleanupFunction = false
      setLoading(true)

      rxios.get(`/company/${companyIdSf}`).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setValues(next)
          saveIsRefresh(false)
          if (!cleanupFunction) setLoading(false)
        },
        (error) => {
          toast.error(error)
          setLoading(false)
        },
        () => {
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [history, rxios, saveIsRefresh],
  )

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  useEffect(() => {
    ;(async () => {
      await loadData()
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (refresh) {
      ;(async () => {
        await loadData()
      })()
    }
    // eslint-disable-next-line
  }, [refresh])

  const title = useCallback(() => {
    return (
      <>
        <Typography variant="h1">{values.name}</Typography>
        <Typography className={classes.titleId}>(id: {companyIdSf})</Typography>
      </>
    )
  }, [classes.titleId, values.name, companyIdSf])

  const handleChangeTab = useCallback(
    (key: string) => {
      history.push(`${routes.COMPANIES}/${companyIdSf}/${key}`)
    },
    [companyIdSf, history],
  )

  return (
    <Grid container className={classes.container}>
      <Grid container className={classes.titleWrap}>
        {title()}
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <TabsComponent
          data={values}
          tabList={filteredTabList}
          clientId={companyIdSf}
          activeTab={activeTab}
          onChangeTab={handleChangeTab}
          refreshProfile={setRefresh}
        />
      )}
    </Grid>
  )
}
